"use client"

import React, { useState, useEffect, useRef } from "react"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog"
import { Button } from "../ui/button"
import { ScrollArea } from "../ui/scroll-area"
import { Tv, ChevronRight, Play, Pause, Volume2, VolumeX, Globe, Pin, PinOff, X, PanelLeft, PanelLeftClose, SkipBack, SkipForward, Info, Captions, MessageSquare } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"
import { AsciiBanner } from "./ascii-banner"
import { cn, getSharedCookie, setSharedCookie } from "../lib/utils"
import { useSidebarUser, updateUserPreferences } from "../lib/auth"
import type { NewsSource, NewsCategory } from "./live-news-types"
import { buildNewsData, REGION_ORDER } from "./live-news-types"
import { allYouTubeSources } from "./live-news-youtube-streams"
import { allHlsSources } from "./live-news-hls-streams"

// ─────────────────────────────────────────────────────────────────────────────
// Build the combined, region-ordered news data from the two stream scripts.
// To add/remove/change channels: edit live-news-youtube-streams.ts or
// live-news-hls-streams.ts.
// ─────────────────────────────────────────────────────────────────────────────
const allSources: NewsSource[] = [...allHlsSources, ...allYouTubeSources]

// Helper for explicit sorting within regions
const getSourceOrder = (source: NewsSource) => {
    const name = source.name
    const region = source.region

    if (region === "Business") {
        const order = [
            "Bloomberg TV", "Bloomberg Plus", "Fox Business", "CNBC", "Yahoo Finance",
            "Schwab Network / TD Ameritrade", "CNBC Europe", "CNBC Arabic", "CNBC Asia", "NDTV Profit",
            "CNBC-TV18", "CNBC TV18 Prime", "CNBC Indonesia", "CNBC Brazil", "CNBC Marathon"
        ]
        const index = order.indexOf(name)
        return index === -1 ? 999 : index
    }

    if (region === "Middle East") {
        if (name === "TRT World") return 0
        if (name === "i24") return 1
        return 2 // Al Jazeera etc
    }

    if (region === "UK") {
        if (name === "BBC News") return 0
        if (name === "Sky News") return 1
        if (name === "GBN (GB News)") return 2
        return 3
    }

    if (region === "China") {
        if (name === "CCTV+") return 0
        if (name === "CGTN") return 1
        return 2
    }

    return 999
}

// Sort by canonical region order, then by internal source order
const BUSINESS_ORDER = [
    "Bloomberg TV", "Bloomberg Plus", "Fox Business", "CNBC", "TD Ameritrade",
    "Yahoo Finance", "Schwab Network / TD Ameritrade", "CNBC Europe", "BFM Business", "RBK",
    "CNBC Arabic", "Bloomberg Turkey", "Bloomberg Saudi Arabia (Asharq)", "CNBC Asia",
    "NDTV Profit", "CNBC-TV18", "CNBC-TV18 Prime", "Z-Business",
    "CNBC Indonesia", "Bloomberg Mongolia", "CNBC Brazil", "Cheddar News", "Bloomberg QuickTake", "CNBC Marathon"
];

const newsData = buildNewsData(allSources).sort((a, b) => {
    const indexA = REGION_ORDER.indexOf(a.region);
    const indexB = REGION_ORDER.indexOf(b.region);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
}).map(category => {
    if (category.region === "Business") {
        return {
            ...category,
            sources: [...category.sources].sort((a, b) => {
                const indexA = BUSINESS_ORDER.indexOf(a.name);
                const indexB = BUSINESS_ORDER.indexOf(b.name);
                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                if (indexA !== -1) return -1;
                if (indexB !== -1) return 1;
                return a.name.localeCompare(b.name);
            })
        }
    }
    return category;
});

const NEWS_STATE_COOKIE = "AskFinz_news_state"
const AUTO_DELAY_SECONDS = 30

export function LiveNews() {
    const { preferences } = useSidebarUser() || {}
    const [selectedCategory, setSelectedCategory] = useState<NewsCategory>(newsData[0])
    const [selectedSource, setSelectedSource] = useState<NewsSource>(newsData[0].sources[0])
    const [isPlaying, setIsPlaying] = useState(true)
    const [isMuted, setIsMuted] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [showSidebar, setShowSidebar] = useState(true)
    const [isSidebarSticky, setIsSidebarSticky] = useState(false)
    const [isPinned, setIsPinned] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [streamError, setStreamError] = useState(false)
    const [resolvedYoutubeId, setResolvedYoutubeId] = useState<string | null>(null)
    const [isResolving, setIsResolving] = useState(false)
    const [showControls, setShowControls] = useState(true)
    const controlsTimerRef = useRef<NodeJS.Timeout | null>(null)
    const sidebarTimerRef = useRef<NodeJS.Timeout | null>(null)
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const videoRef = useRef<HTMLVideoElement>(null)
    const miniVideoRef = useRef<HTMLVideoElement>(null)
    const hlsRef = useRef<any>(null)
    const miniHlsRef = useRef<any>(null)
    const isInternalSync = useRef(false)

    // Derived sequence array for Next/Prev channel navigation
    const flatSources = React.useMemo(() => {
        return newsData.flatMap(cat => cat.sources.map(src => ({ ...src, category: cat })))
    }, [])

    const currentIndex = flatSources.findIndex(s => s.url === selectedSource.url)

    const playNext = () => {
        if (currentIndex < flatSources.length - 1) {
            const next = flatSources[currentIndex + 1]
            setSelectedCategory(next.category as any)
            setSelectedSource(next)
            setIsPlaying(true)
            setIsLoading(true)
        }
    }

    const playPrev = () => {
        if (currentIndex > 0) {
            const prev = flatSources[currentIndex - 1]
            setSelectedCategory(prev.category as any)
            setSelectedSource(prev)
            setIsPlaying(true)
            setIsLoading(true)
        }
    }

    const wakeControls = () => {
        setShowControls(true)
        if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current)
        controlsTimerRef.current = setTimeout(() => {
            setShowControls(false)
        }, 3000)
    }

    // Auto-Retry Logic: If stuck loading for 15s without an explicit error, trigger an attempt to reconnect.
    useEffect(() => {
        let retryTimeout: NodeJS.Timeout;
        if (isLoading && !isResolving && !streamError && selectedSource) {
            retryTimeout = setTimeout(() => {
                if (selectedSource.type === 'hls' && hlsRef.current) {
                    hlsRef.current.recoverMediaError();
                    hlsRef.current.startLoad();
                } else if (selectedSource.type === 'youtube') {
                    // Force iframe to reload by temporarily setting streamError then resetting
                    setStreamError(true);
                    setTimeout(() => setStreamError(false), 500);
                }
            }, 15000);
        }
        return () => clearTimeout(retryTimeout);
    }, [isLoading, isResolving, streamError, selectedSource]);

    // Robust HLS Initialization
    const initHls = (video: HTMLVideoElement | null, hlsRef: React.MutableRefObject<any>, isMini: boolean = false) => {
        if (!video || !selectedSource || selectedSource.type !== "hls") return

        const hlsUrl = selectedSource.url
        if ((window as any).Hls && (window as any).Hls.isSupported()) {
            if (hlsRef.current) hlsRef.current.destroy()

            const hls = new (window as any).Hls({
                enableWorker: false, // Disabled for better sync on proxied streams
                lowLatencyMode: false,
                autoStartLoad: true,
                startLevel: -1,
                capLevelToPlayerSize: true,
                debug: false,
                testBandwidth: true,
                progressive: true,
                manifestLoadingMaxRetry: 5,
                levelLoadingMaxRetry: 5,
                fragLoadingMaxRetry: 5,
                fragLoadingRetryDelay: 2000,
                manifestLoadingRetryDelay: 2000,
                xhrSetup: (xhr: any, url: string) => {
                    xhr.withCredentials = false;
                    // Some CDNs require specific headers for HLS
                    if (url.includes('warnermediacdn.com') || url.includes('cnbcindonesia.com')) {
                        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
                    }
                }
            })

            hlsRef.current = hls
            hls.loadSource(hlsUrl)
            hls.attachMedia(video)

            hls.on((window as any).Hls.Events.MANIFEST_PARSED, () => {
                // Stream Source Masking via Blob URL
                const blob = new Blob([hls.url], { type: 'application/x-mpegURL' });
                const blobUrl = URL.createObjectURL(blob);
                if (video.src !== blobUrl) {
                    // Note: We don't actually set video.src to blobUrl because hls.js manages the attachment.
                    // This is for demonstration of identifying the blob as the source if someone inspects.
                }

                if (isPlaying) {
                    // Special case for CNN: Stop when pinned
                    if (selectedSource.name === "CNN" && isPinned && !isMini) {
                        video.pause();
                        return;
                    }
                    // Optimize play control: ensuring it resumes cleanly
                    video.play().catch(e => {
                        if (selectedSource.name === "CNN") {
                            // Retry logic for CNN if it stops
                            setTimeout(() => video.play().catch(() => { }), 2000);
                        }
                    })

                    // Implement Auto-Delay: Seek back if it's the first play to improve stability
                    if (video.duration > AUTO_DELAY_SECONDS) {
                        video.currentTime = Math.max(0, video.duration - AUTO_DELAY_SECONDS);
                    }
                }
                if (!isMini) setIsLoading(false)
            })

            hls.on((window as any).Hls.Events.ERROR, (_: any, data: any) => {
                if (data.fatal) {
                    switch (data.type) {
                        case (window as any).Hls.ErrorTypes.NETWORK_ERROR:
                            if (selectedSource.fallbackUrl) {
                                const fallbackSrc: NewsSource = {
                                    ...selectedSource,
                                    url: selectedSource.fallbackUrl,
                                    type: selectedSource.fallbackType || 'hls',
                                    fallbackUrl: undefined, // Avoid circular fallback
                                    name: `${selectedSource.name} (Backup)`
                                };
                                setSelectedSource(fallbackSrc);
                            } else {
                                hls.startLoad();
                            }
                            break
                        case (window as any).Hls.ErrorTypes.MEDIA_ERROR:
                            hls.recoverMediaError()
                            // For CNN, try a harder reset if it's still stuck
                            if (selectedSource.name === "CNN") {
                                setTimeout(() => {
                                    if (video.paused && isPlaying) {
                                        hls.swapAudioCodec();
                                        hls.recoverMediaError();
                                    }
                                }, 3000);
                            }
                            break
                        default:
                            if (!isMini) {
                                // For CNN/MS Now, don't immediately show error, try fallback or reload
                                if (selectedSource.fallbackUrl) {
                                    const fallbackSrc: NewsSource = {
                                        ...selectedSource,
                                        url: selectedSource.fallbackUrl,
                                        type: selectedSource.fallbackType || 'hls',
                                        fallbackUrl: undefined,
                                        name: `${selectedSource.name} (Backup)`
                                    };
                                    setSelectedSource(fallbackSrc);
                                } else if (selectedSource.name.includes("CNN") || selectedSource.name.includes("MS Now")) {
                                    hls.loadSource(hlsUrl);
                                    hls.startLoad();
                                } else {
                                    setStreamError(true)
                                }
                            }
                            break
                    }
                }
            })
        } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
            video.src = hlsUrl
            video.addEventListener("loadedmetadata", () => {
                if (isPlaying) video.play().catch(() => { })
                if (!isMini) setIsLoading(false)
            }, { once: true })
        }
    }

    // Dynamic HLS Support
    useEffect(() => {
        if (!typeof window || !selectedSource) return
        if (selectedSource.type !== "hls") {
            if (hlsRef.current) hlsRef.current.destroy()
            if (miniHlsRef.current) miniHlsRef.current.destroy()
            return
        }

        const scriptId = "hls-js-script"

        // Helper to delay init until refs are likely ready
        const startHls = () => {
            // Main View
            if (!isPinned || dialogOpen) {
                if (miniHlsRef.current) {
                    miniHlsRef.current.destroy()
                    miniHlsRef.current = null
                }
                // Small delay to ensure DOM is ready after state transitions
                setTimeout(() => initHls(videoRef.current, hlsRef), 100)
            }

            // Mini View
            if (isPinned && !dialogOpen) {
                if (hlsRef.current) {
                    hlsRef.current.destroy()
                    hlsRef.current = null
                }
                // Popovers/Portals need a bit more time to mount the video element
                setTimeout(() => initHls(miniVideoRef.current, miniHlsRef, true), 300)
            }
        }

        if (!document.getElementById(scriptId)) {
            const script = document.createElement("script")
            script.id = scriptId
            script.src = "https://cdn.jsdelivr.net/npm/hls.js@1.5.17"
            script.async = true
            script.onload = startHls
            document.head.appendChild(script)
        } else if ((window as any).Hls) {
            startHls()
        }

        return () => {
            // Clean up both on unmount/source change
            if (hlsRef.current) hlsRef.current.destroy()
            if (miniHlsRef.current) miniHlsRef.current.destroy()
        }
    }, [selectedSource, isPlaying, isPinned, dialogOpen])

    // Load state from preferences (with cookie fallback) on mount / preference change
    useEffect(() => {
        const storedCookie = getSharedCookie(NEWS_STATE_COOKIE)
        let data: any = null
        if (storedCookie) {
            try { data = JSON.parse(storedCookie) } catch (e) { }
        }

        // Prioritize backend preferences
        const backendPrefs = preferences?.liveNews;
        if (backendPrefs && !isInternalSync.current) {
            try {
                if (backendPrefs.category) {
                    const cat = newsData.find(c => c.region === backendPrefs.category) || newsData[0]
                    setSelectedCategory(cat)
                    if (backendPrefs.sourceUrl && (!selectedSource || selectedSource.url !== backendPrefs.sourceUrl)) {
                        const src = cat.sources.find(s => s.url === backendPrefs.sourceUrl) || cat.sources[0]
                        setSelectedSource(src)
                        setIsLoading(true)
                    }
                }
                if (backendPrefs.pinned !== undefined && backendPrefs.pinned !== isPinned) {
                    setIsPinned(!!backendPrefs.pinned)
                }
            } catch (e) { console.debug("News sync error:", e) }
            return; // Backend preferences take precedence
        }
        isInternalSync.current = false;

        if (data) {
            try {
                if (data.category) {
                    const cat = newsData.find(c => c.region === data.category) || newsData[0]
                    setSelectedCategory(cat)
                    if (data.sourceUrl) {
                        const src = cat.sources.find(s => s.url === data.sourceUrl) || cat.sources[0]
                        setSelectedSource(src)
                    }
                }
                if (data.pinned !== undefined) setIsPinned(!!data.pinned)
            } catch (e) { console.debug("News sync error:", e) }
        }
    }, [preferences?.liveNews])

    // Persist news state to backend preferences & shared cookie
    const syncState = (source: NewsSource, category: NewsCategory, pinned: boolean) => {
        const state = {
            category: category.region,
            sourceUrl: source.url,
            pinned: pinned
        };
        isInternalSync.current = true;
        setSharedCookie(NEWS_STATE_COOKIE, JSON.stringify(state));
        updateUserPreferences({ liveNews: state });
    };

    useEffect(() => {
        syncState(selectedSource, selectedCategory, isPinned);
    }, [selectedCategory.region, selectedSource.url, isPinned])

    // Listen to iframe postMessage for YouTube Player API events
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== "https://www.youtube.com") return

            try {
                const data = JSON.parse(event.data)
                if (data.event === "onStateChange" && data.info === 0) {
                    // Video Ended / Stream Offline
                    setStreamError(true)
                } else if (data.event === "onError") {
                    // Stream Error / Unplayable
                    setStreamError(true)
                }
            } catch (e) { }
        }

        window.addEventListener("message", handleMessage)
        return () => window.removeEventListener("message", handleMessage)
    }, [])

    // Reset error state when source changes and auto-fetch live IDs for YouTube channels
    useEffect(() => {
        setStreamError(false)
        setResolvedYoutubeId(null)

        if (selectedSource.type !== 'youtube') return

        const autoFetchId = async () => {
            // Extract the videoId already baked into the url (works even after container restart)
            const extractIdFromUrl = (url: string): string | null => {
                if (!url) return null
                let m = url.match(/[?&]v=([A-Za-z0-9_-]{11})/)
                if (m) return m[1]
                m = url.match(/youtube\.com\/live\/([A-Za-z0-9_-]{11})/)
                if (m) return m[1]
                m = url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/)
                if (m) return m[1]
                m = url.match(/embed\/([A-Za-z0-9_-]{11})/)
                if (m) return m[1]
                return null
            }

            if (selectedSource.channelId) {
                setIsResolving(true)
                try {
                    // Strategy 1: Backend proxy (RSS → redirect → scrape)
                    // Added AbortController for 5s timeout to prevent hanging UI on DNS failure
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 5000);

                    const response = await fetch(`/api/youtube/live/${selectedSource.channelId}`, {
                        signal: controller.signal
                    });
                    clearTimeout(timeoutId);

                    if (response.ok) {
                        const data = await response.json()
                        if (data.videoId) {
                            setResolvedYoutubeId(data.videoId)
                            setIsResolving(false)
                            setStreamError(false) // Ensure clean state
                            return
                        }
                    }
                } catch (e) {
                    console.debug("YouTube proxy failed, using built-in URL fallback:", e)
                } finally {
                    setIsResolving(false)
                }

                // Strategy 2: Use the videoId baked into the url field
                const storedId = extractIdFromUrl(selectedSource.url)
                if (storedId) {
                    setResolvedYoutubeId(storedId)
                }
                // Strategy 3 (implicit): getEmbedUrl will use live_stream?channel= as last resort
            } else {
                // No channelId — just extract from url directly
                const storedId = extractIdFromUrl(selectedSource.url)
                if (storedId) setResolvedYoutubeId(storedId)
            }
        }

        autoFetchId()
    }, [selectedSource.url, selectedSource.channelId])

    const getEmbedUrl = (source: NewsSource) => {
        if (source.type === "youtube") {
            const originStr = typeof window !== 'undefined' ? window.location.origin : ''
            // Minimal YouTube parameters + JsApi + Autoplay for sync
            const params = `mute=${isMuted ? 1 : 0}&autoplay=1&enablejsapi=1&controls=1&rel=0&showinfo=0&fs=1`

            // Tier 1: Use the dynamically resolved live video ID (from proxy or url extraction)
            if (resolvedYoutubeId) {
                return `https://www.youtube-nocookie.com/embed/${resolvedYoutubeId}?${params}`
            }

            // Tier 2: Use the channelId live_stream embed
            if (source.channelId) {
                return `https://www.youtube-nocookie.com/embed/live_stream?channel=${source.channelId}&${params}`
            }

            // Tier 3: Extract videoId from the url string
            let videoId = ""
            const urlPatterns = [
                /[?&]v=([A-Za-z0-9_-]{11})/,
                /youtube\.com\/live\/([A-Za-z0-9_-]{11})/,
                /youtu\.be\/([A-Za-z0-9_-]{11})/,
                /embed\/([A-Za-z0-9_-]{11})/,
            ]
            for (const pattern of urlPatterns) {
                const m = source.url.match(pattern)
                if (m) { videoId = m[1]; break }
            }

            if (!videoId && source.url.includes("embed/")) {
                const cleanUrl = source.url.replace("youtube.com", "youtube-nocookie.com")
                return `${cleanUrl}${cleanUrl.includes("?") ? "&" : "?"}${params}`
            }

            return `https://www.youtube-nocookie.com/embed/${videoId}?${params}`
        }
        if (source.type === "rumble") {
            // Rumble embed parameters to minimize branding
            return `${source.url}&autoplay=1&muted=${isMuted ? 1 : 0}&controls=0&rel=0`
        }
        // For direct iframe embeds (e.g. NDTV, India Today) — return URL as-is (autoplay handled in URL)
        return source.url
    }

    const sendCommand = (command: any) => {
        if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(JSON.stringify(command), "*")
        }
    }

    const togglePlay = () => {
        const nextState = !isPlaying
        setIsPlaying(nextState)
        if (selectedSource.type === "youtube") {
            sendCommand({ event: "command", func: nextState ? "playVideo" : "pauseVideo" })
        } else if (selectedSource.type === "rumble") {
            sendCommand({ method: nextState ? "play" : "pause" })
        } else if (selectedSource.type === "hls") {
            if (videoRef.current) {
                if (nextState) videoRef.current.play().catch(() => { })
                else videoRef.current.pause()
            }
            if (miniVideoRef.current) {
                if (nextState) miniVideoRef.current.play().catch(() => { })
                else miniVideoRef.current.pause()
            }
        }
    }

    const toggleMute = () => {
        const nextState = !isMuted
        setIsMuted(nextState)
        if (selectedSource.type === "youtube") {
            sendCommand({ event: "command", func: nextState ? "mute" : "unMute" })
        } else if (selectedSource.type === "rumble") {
            sendCommand({ method: nextState ? "mute" : "unmute" })
        } else if (selectedSource.type === "hls") {
            if (videoRef.current) videoRef.current.muted = nextState
            if (miniVideoRef.current) miniVideoRef.current.muted = nextState
        }
    }

    const handleIframeLoad = () => {
        // Adjusted delay to 7 seconds to balance branding suppression and user wait time
        setTimeout(() => setIsLoading(false), 7000)

        // For iframe-type sources (e.g. NDTV), send unMute immediately and with retries
        // to catch the player after it finishes initialising internally
        if (selectedSource.type === "iframe" && iframeRef.current?.contentWindow) {
            const sendUnMute = () => {
                iframeRef.current?.contentWindow?.postMessage(
                    '{"event":"command","func":"unMute","args":""}',
                    '*'
                )
            }
            sendUnMute()
            setTimeout(sendUnMute, 1000)
            setTimeout(sendUnMute, 3000)
        }
    }

    // Auto-hide sidebar logic
    useEffect(() => {
        const startTimer = () => {
            if (isSidebarSticky) return
            if (sidebarTimerRef.current) clearTimeout(sidebarTimerRef.current)
            sidebarTimerRef.current = setTimeout(() => {
                setShowSidebar(false)
            }, 3000)
        }

        startTimer()

        return () => {
            if (sidebarTimerRef.current) clearTimeout(sidebarTimerRef.current)
        }
    }, [])

    // First-click unmute for iframe sources (e.g. NDTV) that start muted due to browser policy
    useEffect(() => {
        if (selectedSource.type !== "iframe") return
        const handleFirstClick = () => {
            if (iframeRef.current?.contentWindow) {
                iframeRef.current.contentWindow.postMessage(
                    '{"event":"command","func":"unMute","args":""}',
                    '*'
                )
            }
        }
        window.addEventListener('click', handleFirstClick, { once: true })
        return () => window.removeEventListener('click', handleFirstClick)
    }, [selectedSource])

    const handleMouseMove = () => {
        if (!showSidebar) setShowSidebar(true)

        if (isSidebarSticky) return

        if (sidebarTimerRef.current) clearTimeout(sidebarTimerRef.current)
        sidebarTimerRef.current = setTimeout(() => {
            setShowSidebar(false)
        }, 3000)
    }

    const togglePin = () => {
        if (isPinned) {
            setIsPinned(false)
        } else {
            setIsPinned(true)
            setDialogOpen(false) // Close main dialog when pinning
        }
    }

    const trigger = (
        <Button
            variant="ghost"
            size="sm"
            className={cn("h-9 w-9 p-0 relative", isPinned && "text-primary")}
            title="Live News"
        >
            <Tv className="h-4 w-4" />
            {isPinned && <div className="absolute top-1 right-1 h-1.5 w-1.5 bg-primary rounded-full" />}
        </Button>
    )

    return (
        <Popover open={isPinned && !dialogOpen}>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <PopoverTrigger asChild>
                    <DialogTrigger asChild>
                        {trigger}
                    </DialogTrigger>
                </PopoverTrigger>
                <DialogContent
                    className="max-w-[1440px] w-[95vw] h-[80vh] p-0 overflow-hidden flex flex-col sm:flex-row gap-0 bg-black border-none shadow-2xl [&>button]:hidden"
                    onMouseMove={handleMouseMove}
                >
                    <DialogTitle className="sr-only">Live News Player</DialogTitle>
                    <DialogDescription className="sr-only">
                        Watch global live news streams with region-based categories.
                    </DialogDescription>
                    {/* Sidebar */}
                    <div
                        className={cn(
                            "transition-all duration-500 ease-in-out border-r border-border flex flex-col bg-background overflow-hidden shrink-0",
                            showSidebar ? "w-full sm:w-[var(--sidebar-width,16rem)] opacity-100" : "w-0 opacity-0 border-none"
                        )}
                    >
                        <div className="relative flex items-center justify-between px-4 py-3 border-b border-border bg-muted/50">
                            <h2 className="text-xs font-bold flex items-center gap-2 text-foreground uppercase tracking-widest">
                                <Globe className="h-3 w-3 text-primary" />
                                Global News
                            </h2>
                            <Button
                                variant="ghost"
                                size="sm"
                                className={cn(
                                    "h-7 w-7 p-0 transition-colors",
                                    isSidebarSticky ? "text-primary hover:text-primary/80" : "text-muted-foreground hover:text-foreground hover:bg-accent"
                                )}
                                onClick={() => {
                                    const nextSticky = !isSidebarSticky
                                    setIsSidebarSticky(nextSticky)
                                    // Let auto-hide take over if turning off stickiness
                                    if (!nextSticky && sidebarTimerRef.current) {
                                        clearTimeout(sidebarTimerRef.current)
                                        sidebarTimerRef.current = setTimeout(() => setShowSidebar(false), 3000)
                                    }
                                }}
                            >
                                <PanelLeftClose className="h-4 w-4" />
                            </Button>

                            {/* Loading Indicator beneath Sidebar Header */}
                            {(isLoading || isResolving) && !streamError && (
                                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/5 overflow-hidden">
                                    <div className="h-full bg-primary animate-[pulse_1s_ease-in-out_infinite] w-2/3 rounded-r-full" />
                                </div>
                            )}
                        </div>
                        <ScrollArea className="flex-1 w-full">
                            <div className="p-3 space-y-4">
                                {newsData.map((category) => (
                                    <div key={category.region} className="space-y-1">
                                        <div className="px-3 py-1 flex items-center justify-between">
                                            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{category.region}</span>
                                            {selectedCategory.region === category.region && <div className="h-1 w-1 rounded-full bg-primary" />}
                                        </div>
                                        <div className="ml-1 space-y-0.5 border-l border-border pl-2">
                                            {category.sources.map((source) => (
                                                <Button
                                                    key={source.url}
                                                    variant="ghost"
                                                    size="sm"
                                                    className={cn(
                                                        "w-full justify-start text-[11px] font-medium py-1.5 h-auto px-2 transition-colors",
                                                        selectedSource.url === source.url
                                                            ? "bg-primary text-primary-foreground hover:bg-primary/90"
                                                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                                                    )}
                                                    onClick={() => {
                                                        setSelectedSource(source)
                                                        setSelectedCategory(category)
                                                        setIsPlaying(true)
                                                        setIsLoading(true)
                                                    }}
                                                >
                                                    {source.icon ? (
                                                        <img
                                                            src={source.icon}
                                                            alt=""
                                                            className="h-3 w-3 mr-2 rounded-sm object-contain"
                                                            onError={(e) => {
                                                                (e.target as any).style.display = 'none';
                                                            }}
                                                        />
                                                    ) : (
                                                        <Play className={cn("h-2.5 w-2.5 mr-2", selectedSource.url === source.url ? "fill-primary text-primary" : "text-zinc-600")} />
                                                    )}
                                                    {source.name}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </ScrollArea>
                    </div>

                    {/* Main Player Area */}
                    <div className="flex-1 bg-black relative flex items-center justify-center overflow-hidden h-full">
                        <div
                            className="w-full aspect-video relative max-h-full max-w-[1440px] flex items-center justify-center shadow-2xl overflow-hidden"
                            onMouseMove={wakeControls}
                            onTouchStart={wakeControls}
                            onMouseLeave={() => setShowControls(false)}
                        >
                            {/* Toggle Button (when sidebar is closed) */}
                            {!showSidebar && (
                                <div className={cn(
                                    "absolute top-4 left-4 z-40 transition-all duration-700",
                                    (showControls || !isPlaying) ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4 pointer-events-none"
                                )}>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-10 w-10 p-0 rounded-full bg-black/40 hover:bg-black/60 text-white border-white/10 border backdrop-blur-sm"
                                        onClick={() => {
                                            setIsSidebarSticky(true)
                                            setShowSidebar(true)
                                        }}
                                        title="Show Sidebar"
                                    >
                                        <PanelLeft className="h-5 w-5" />
                                    </Button>
                                </div>
                            )}

                            {/* The Player */}
                            {!streamError ? (
                                selectedSource.type === "hls" ? (
                                    <video
                                        ref={videoRef}
                                        className="w-full h-full absolute inset-0 bg-black object-contain"
                                        autoPlay
                                        muted={isMuted}
                                        playsInline
                                        onPlay={() => {
                                            setIsLoading(false)
                                            if (videoRef.current && videoRef.current.duration > AUTO_DELAY_SECONDS) {
                                                videoRef.current.currentTime = Math.max(0, videoRef.current.duration - AUTO_DELAY_SECONDS);
                                            }
                                        }}
                                        onWaiting={() => setIsLoading(true)}
                                        onPlaying={() => setIsLoading(false)}
                                        onError={() => setStreamError(true)}
                                    />
                                ) : (
                                    <iframe
                                        ref={iframeRef}
                                        key={resolvedYoutubeId ?? selectedSource.url}
                                        src={getEmbedUrl(selectedSource)}
                                        className="w-full h-[105%] border-0 absolute -top-[2.5%] inset-x-0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        onLoad={handleIframeLoad}
                                    />
                                )
                            ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center bg-background text-foreground font-mono space-y-6">
                                    <AsciiBanner size="large" />
                                    <p className="text-2xl opacity-70 border-b border-primary/50 pb-2">Live feed unavailable</p>
                                </div>
                            )}

                            {/* Loading State Overlay */}
                            {(isLoading || isResolving) && !streamError && (
                                <div className="absolute inset-0 z-[60] bg-black flex flex-col items-center justify-center">
                                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-white/5 overflow-hidden">
                                        <div className="h-full bg-primary animate-[pulse_1s_ease-in-out_infinite] w-2/3 rounded-r-full" />
                                    </div>
                                    <AsciiBanner size="large" />
                                </div>
                            )}

                            {/* Paused State Overlay */}
                            {!isPlaying && !isLoading && !isResolving && !streamError && (
                                <div className="absolute inset-0 z-[5] bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center gap-4">
                                    <Pause className="h-12 w-12 text-white/40" />
                                    <p className="text-white/40 font-mono text-sm tracking-widest uppercase">Stream Paused</p>
                                </div>
                            )}

                            {/* Transparent Click Interceptor */}
                            <div className="absolute inset-0 z-10 bg-transparent" />

                            {/* Custom Controls UI (Auto-Hide aware) */}
                            <div className={cn(
                                "absolute bottom-0 left-0 right-0 p-6 z-20 bg-gradient-to-t from-black/95 via-black/70 to-transparent transition-all duration-700",
                                (showControls || !isPlaying) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
                            )}>
                                <div className="flex flex-col gap-4">
                                    {/* INFO Overlay (Logo + Source) */}
                                    <div className="flex items-center gap-3">
                                        {selectedSource.icon && (
                                            <div className="w-10 h-10 rounded-md overflow-hidden bg-white/10 shrink-0 flex items-center justify-center shadow-lg">
                                                <img src={selectedSource.icon} className="w-[80%] h-[80%] object-contain" alt="" />
                                            </div>
                                        )}
                                        <div className="flex flex-col min-w-0">
                                            <span className="text-white font-bold text-xl drop-shadow-md truncate">{selectedSource.name}</span>
                                            <span className="text-white/60 font-medium text-sm drop-shadow truncate uppercase tracking-widest">{selectedCategory.region}</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-10 w-10 p-0 rounded-full bg-white/10 hover:bg-white/20 text-white border-white/10 border backdrop-blur-sm"
                                                onClick={playPrev}
                                                disabled={currentIndex === 0 || streamError}
                                                title="Previous Channel"
                                            >
                                                <SkipBack className="h-4 w-4 fill-white text-white" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-10 w-10 p-0 rounded-full bg-white/10 hover:bg-white/20 text-white border-white/10 border backdrop-blur-sm"
                                                onClick={togglePlay}
                                                disabled={streamError}
                                                title={isPlaying ? "Pause" : "Play"}
                                            >
                                                {isPlaying ? <Pause className="h-5 w-5 fill-white" /> : <Play className="h-5 w-5 fill-white text-white" />}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-10 w-10 p-0 rounded-full bg-white/10 hover:bg-white/20 text-white border-white/10 border backdrop-blur-sm"
                                                onClick={playNext}
                                                disabled={currentIndex === flatSources.length - 1 || streamError}
                                                title="Next Channel"
                                            >
                                                <SkipForward className="h-4 w-4 fill-white text-white" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-10 w-10 p-0 rounded-full bg-white/10 hover:bg-white/20 text-white border-white/10 border backdrop-blur-sm"
                                                onClick={toggleMute}
                                                disabled={streamError}
                                                title={isMuted ? "Unmute" : "Mute"}
                                            >
                                                {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                                            </Button>

                                            {/* Live Indicator */}
                                            <div className="ml-4 flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full ${streamError ? 'bg-zinc-500' : 'bg-red-500 animate-[pulse_2s_ease-in-out_infinite]'}`} />
                                                <span className="text-white text-[10px] font-bold uppercase tracking-widest drop-shadow">
                                                    {streamError ? 'Offline' : 'Live'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-10 w-10 p-0 rounded-full bg-white/10 hover:bg-white/20 text-white border-white/10 border backdrop-blur-sm"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    togglePin()
                                                }}
                                                title={isPinned ? "Unpin" : "Pin to Mini-View"}
                                            >
                                                {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog >

            {isPinned && selectedSource && (
                <PopoverContent
                    side="bottom"
                    align="end"
                    alignOffset={-12}
                    className="w-[400px] p-0 bg-black border-white/10 shadow-2xl overflow-hidden mt-3"
                    sideOffset={12}
                >
                    <div className="aspect-video relative group">
                        {!streamError ? (
                            selectedSource.type === "hls" ? (
                                <video
                                    ref={miniVideoRef}
                                    className={cn(
                                        "w-full h-full absolute inset-0 bg-black",
                                        selectedSource.name === "MS Now" ? "object-contain" : "object-contain"
                                    )}
                                    autoPlay
                                    muted={isMuted}
                                    playsInline
                                />
                            ) : (
                                <iframe
                                    key={resolvedYoutubeId ?? selectedSource.url}
                                    src={getEmbedUrl(selectedSource)}
                                    className="w-full h-[105%] border-0 absolute -top-[2.5%] inset-x-0 pointer-events-none"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    referrerPolicy="strict-origin-when-cross-origin"
                                />
                            )
                        ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-950 text-white font-mono space-y-2">
                                <AsciiBanner size="small" />
                                <p className="text-xs opacity-70">Live feed unavailable</p>
                            </div>
                        )}

                        {/* Mini-Viewer Loading State Overlay */}
                        {(isLoading || isResolving) && !streamError && (
                            <div className="absolute inset-0 z-[60] bg-black flex items-center justify-center">
                                <AsciiBanner size="small" />
                            </div>
                        )}
                        {/* Overlay to catch clicks and show controls */}
                        <div className="absolute inset-0 z-10 bg-transparent" />

                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col justify-end p-4 z-20">
                            <div className="flex flex-col gap-3">
                                <div className="flex justify-between items-start">
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-[10px] text-zinc-400 uppercase font-bold tracking-widest pb-0.5">
                                            {selectedCategory?.region}
                                        </span>
                                        <span className="text-xs text-white font-bold truncate max-w-[180px] drop-shadow-md">
                                            {selectedSource.name}
                                        </span>
                                    </div>
                                    <div className="flex gap-1.5">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0 rounded-full bg-white/10 hover:bg-white/20 text-white border-white/5 border backdrop-blur-sm"
                                            onClick={() => {
                                                setIsPinned(false)
                                                setDialogOpen(true)
                                            }}
                                            title="Back to Full View"
                                        >
                                            <Tv className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0 rounded-full bg-white/10 hover:bg-red-500/40 text-white border-white/5 border backdrop-blur-sm"
                                            onClick={togglePin}
                                            title="Close"
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="flex gap-2 items-center">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 rounded-full bg-white/10 hover:bg-white/20 text-white border-white/5 border backdrop-blur-sm"
                                            onClick={togglePlay}
                                            disabled={streamError}
                                        >
                                            {isPlaying ? <Pause className="h-4 w-4 fill-white text-white" /> : <Play className="h-4 w-4 fill-white text-white" />}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 rounded-full bg-white/10 hover:bg-white/20 text-white border-white/5 border backdrop-blur-sm"
                                            onClick={toggleMute}
                                            disabled={streamError}
                                        >
                                            {isMuted ? <VolumeX className="h-4 w-4 text-white" /> : <Volume2 className="h-4 w-4 text-white" />}
                                        </Button>
                                    </div>
                                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full h-fit ${streamError ? 'bg-zinc-600' : 'bg-red-600'}`}>
                                        <div className={`h-1 w-1 rounded-full ${streamError ? 'bg-zinc-300' : 'bg-white animate-pulse'}`} />
                                        <span className="text-[8px] font-bold text-white uppercase tracking-widest leading-none">
                                            {streamError ? 'Offline' : 'Live'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </PopoverContent>
            )
            }
        </Popover >
    )
}
